import {
  Component,
  output,
  input,
  signal,
  inject,
  effect,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FormFolder } from '../../models';
import { FormFolderService } from '../../services';
import { ToastMessageService } from '@fitness-force/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-folder-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TranslateModule,
  ],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [style]="{ width: '500px' }"
      [header]="(isEditMode() ? 'SURVEY_FORMS.EDIT_FOLDER' : 'SURVEY_FORMS.CREATE_FOLDER') | translate"
      (onHide)="onClose()"
      [resizable]="false"
      data-testid="folder-dialog"
    >
      <form [formGroup]="folderForm" (ngSubmit)="onSubmit()">
        <div class="mb-4">
          <label class="block mb-2 font-medium" for="folder-name">
            {{ 'SURVEY_FORMS.FOLDER_NAME' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <input
            pInputText
            id="folder-name"
            formControlName="name"
            class="w-full"
            [placeholder]="'SURVEY_FORMS.FOLDER_NAME_PLACEHOLDER' | translate"
            aria-label="folder-name-input"
            data-testid="folder-name-input"
          />
          @if (
            folderForm.get('name')?.touched && folderForm.get('name')?.invalid
          ) {
            <small class="text-red-500 mt-1 block">
              {{ 'SURVEY_FORMS.FOLDER_NAME_REQUIRED' | translate }}
            </small>
          }
        </div>

        <div class="mb-4">
          <label class="block mb-2 font-medium" for="parent-folder">
            {{ 'SURVEY_FORMS.PARENT_FOLDER' | translate }}
          </label>
          <p-select
            [options]="parentFolders()"
            formControlName="parent_id"
            optionLabel="name"
            optionValue="id"
            [placeholder]="'SURVEY_FORMS.SELECT_PARENT_FOLDER' | translate"
            [showClear]="true"
            class="w-full"
            aria-label="parent-folder-select"
            data-testid="parent-folder-select"
          />
        </div>

        <div class="flex justify-content-end gap-2 mt-4">
          <button
            pButton
            type="button"
            [label]="'SURVEY_FORMS.CANCEL' | translate"
            class="p-button-text"
            (click)="onClose()"
            aria-label="cancel-folder-button"
            data-testid="cancel-folder-button"
          ></button>
          <button
            pButton
            type="submit"
            [label]="(isEditMode() ? 'SURVEY_FORMS.UPDATE' : 'SURVEY_FORMS.CREATE') | translate"
            [disabled]="folderForm.invalid || submitting()"
            aria-label="submit-folder-button"
            data-testid="submit-folder-button"
          ></button>
        </div>
      </form>
    </p-dialog>
  `,
  styles: [],
})
export class FolderManagementComponent {
  private readonly fb = inject(FormBuilder);
  private readonly folderService = inject(FormFolderService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);

  // Inputs
  visible = input.required<boolean>();
  tenantId = input.required<string>();
  editFolder = input<FormFolder | null>(null);
  parentFolders = input<FormFolder[]>([]);

  // Outputs
  visibleChange = output<boolean>();
  folderSaved = output<void>();

  // Signals
  protected readonly submitting = signal(false);
  protected readonly isEditMode = signal(false);

  protected readonly folderForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    parent_id: [null as number | null],
  });

  constructor() {
    // Use effect to react to editFolder changes
    effect(() => {
      const folder = this.editFolder();
      if (folder) {
        this.isEditMode.set(true);
        this.folderForm.patchValue({
          name: folder.name ?? '',
          parent_id: folder.parent_id ?? null,
        });
      } else {
        this.isEditMode.set(false);
        this.folderForm.reset();
      }
    });
  }

  protected onSubmit(): void {
    if (this.folderForm.invalid) {
      Object.keys(this.folderForm.controls).forEach((field) => {
        this.folderForm.get(field)?.markAsTouched();
      });
      return;
    }

    this.submitting.set(true);
    const payload = this.folderForm.getRawValue();
    const folder = this.editFolder();

    const operation =
      this.isEditMode() && folder
        ? this.folderService.updateFolder(
            this.tenantId(),
            String(folder.id ?? ''),
            payload,
          )
        : this.folderService.createFolder(this.tenantId(), payload);

    operation.subscribe({
      next: () => {
        const messageKey = this.isEditMode() 
          ? 'SURVEY_FORMS.FOLDER_UPDATED' 
          : 'SURVEY_FORMS.FOLDER_CREATED';
        this.translate.get(messageKey).subscribe((message: string) => {
          this.toastService.showSuccess(message);
        });
        this.folderSaved.emit();
        this.onClose();
      },
      error: (error: any) => {
        this.toastService.showError(error);
        this.submitting.set(false);
      },
      complete: () => {
        this.submitting.set(false);
      },
    });
  }

  protected onClose(): void {
    this.folderForm.reset();
    this.isEditMode.set(false);
    this.visibleChange.emit(false);
  }
}
